package com.downdogapp

import com.downdogapp.CSSImage.Gradient.LinearColorStop
import com.downdogapp.CSSImage.Gradient.LinearGradient
import com.downdogapp.Color.Companion.WHITE
import com.downdogapp.Display.INLINE
import com.downdogapp.Display.NONE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.INHERIT
import com.downdogapp.Size.Percent
import com.downdogapp.Size.Pixels
import kotlinx.browser.document
import kotlinx.dom.appendElement
import org.w3c.dom.*
import org.w3c.dom.events.Event
import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.InvocationKind.EXACTLY_ONCE
import kotlin.contracts.contract

/**
 * AUTO-GENERATED DO NOT EDIT!
 * from [com.downdogapp.tasks.HtmlBuilder]
 */
// @formatter:off 
sealed class Size {

  abstract val str: String

  override fun toString() = str

  abstract class SimpleSize(override val str: String) : Size()

  object AUTO : SimpleSize("auto")
  object INHERIT : SimpleSize("inherit")
  object FIT_CONTENT : SimpleSize("fit-content")

  class Pixels(private val num: Number) : SimpleSize("${num}px"), Comparable<Pixels> {

    operator fun plus(lhs: Pixels) = Pixels(num.toDouble() + lhs.num.toDouble())
    operator fun minus(lhs: Pixels) = Pixels(num.toDouble() - lhs.num.toDouble())
    operator fun times(lhs: Number) = Pixels(num.toDouble() * lhs.toDouble())
    operator fun div(lhs: Number) = Pixels(num.toDouble() / lhs.toDouble())

    override operator fun compareTo(other: Pixels) = num.toDouble().compareTo(other.num.toDouble())

    override fun equals(other: Any?) = num.toDouble() == (other as? Pixels)?.num?.toDouble()
    override fun hashCode() = num.toDouble().hashCode()

    fun toInt() = num.toInt()
    fun toDouble() = num.toDouble()
  }

  private class Em(num: Number) : SimpleSize("${num}em")
  class Percent(num: Number) : SimpleSize("$num%")

  private class ViewWidth(num: Number) : SimpleSize("${num}vw")
  private class ViewHeight(num: Number) : SimpleSize("${num}vh")
  private class ViewMin(num: Number) : SimpleSize("${num}vmin")
  private class ViewMax(num: Number) : SimpleSize("${num}vmax")

  private class Calc(addition: Boolean,
                     first: Size,
                     second: Size) : Size() {

    private val operator = " ${if (addition) '+' else '-'} "

    private val innerExpr = "$first$operator$second"

    override val str =
      "calc(" +
      when (first) {
        is Calc -> first.innerExpr
        else -> first.str
      } +
      operator +
      when (second) {
        is Calc -> second.innerExpr
        else -> second.str
      } +
      ')'
  }

  operator fun plus(rhs: Size): Size = Calc(true, this, rhs)
  operator fun minus(rhs: Size): Size = Calc(false, this, rhs)

  companion object {

    fun px(num: Number): Pixels = Pixels(num)
    fun em(num: Number): Size = Em(num)
    fun percent(num: Number): Percent = Percent(num)
    fun vw(num: Number): Size = ViewWidth(num)
    fun vh(num: Number): Size = ViewHeight(num)
    fun vmin(num: Number): Size = ViewMin(num)
    fun vmax(num: Number): Size = ViewMax(num)

    fun fromString(str: String) = when {
      str.endsWith("px") -> Pixels(str.substringBefore("px").toDouble())
      str.endsWith("em") -> Pixels(str.substringBefore("em").toDouble())
      str.endsWith("%") -> Percent(str.substringBefore("%").toDouble())
      str.endsWith("vw") -> ViewWidth(str.substringBefore("vw").toDouble())
      str.endsWith("vh") -> ViewHeight(str.substringBefore("vh").toDouble())
      str.endsWith("vmin") -> ViewMin(str.substringBefore("vmin").toDouble())
      str.endsWith("vmax") -> ViewMax(str.substringBefore("vmax").toDouble())
      else -> when (str) {
        "auto" -> AUTO
        "inherit" -> INHERIT
        else -> null
      }
    }
  }
}

sealed class ZIndex(private val str: String) {

  object AUTO : ZIndex("auto")
  object INITIAL : ZIndex("initial")
  object INHERIT : ZIndex("inherit")

  private class ZIndexValue(num: Int) : ZIndex(num.toString())

  override fun equals(other: Any?) = str == (other as? ZIndex)?.str
  override fun hashCode() = str.hashCode()

  override fun toString() = str

  companion object {
    fun zVal(num: Int): ZIndex = ZIndexValue(num)

    fun fromString(str: String) =
      if (str.toIntOrNull() != null)
        ZIndexValue(str.toInt())
      else
        when (str) {
          "auto" -> AUTO
          "initial" -> INITIAL
          "inherit" -> INHERIT
          else -> null
        }
  }
}

sealed class Border(private val str: String) {

  object NONE : Border("none")

  private class Solid(size: Size, color: Color) : Border("$size solid $color")

  override fun toString() = str

  companion object {
    fun solid(size: Size, color: Color): Border = Solid(size, color)
  }
}

sealed class TextShadow(private val str: String) {

  object NONE : TextShadow("none")

  private class Shadow(horizontal: Size, vertical: Size, blur: Size?, color: Color) : TextShadow("$horizontal $vertical ${blur ?: "0"} $color")

  override fun toString() = str

  companion object {
    fun shadow(horizontal: Size, vertical: Size, blur: Size? = null, color: Color): TextShadow = Shadow(horizontal, vertical, blur, color)
  }
}

sealed class BoxShadow(private val str: String) {

  object NONE : BoxShadow("none")

  private class Shadow(horizontal: Size, vertical: Size, blur: Size?, spread: Size?, inset: Boolean, color: Color) : BoxShadow("${ if (inset) "inset " else "" }$horizontal $vertical ${blur ?: "0"} ${spread ?: "0"} $color")

  override fun toString() = str

  companion object {
    fun boxShadow(horizontal: Size, vertical: Size, blur: Size? = null, spread: Size? = null, inset: Boolean = false, color: Color): BoxShadow = Shadow(horizontal, vertical, blur, spread, inset, color)
  }
}

sealed class CSSImage(private val str: String) {

  class Url(url: String) : CSSImage("url($url)")

  sealed class Gradient(private val str: String) : CSSImage(str) {
    class LinearGradient(colorStopList: List<LinearColorStop>, direction: Direction) : Gradient("linear-gradient(to ${direction.name.lowercase()}, ${colorStopList.joinToString()})")
    class LinearColorStop(private val str: String) {
      override fun toString() = str
    }

    override fun toString() = str

    companion object {
      fun linearColorStop(color: Color, length: Percent? = null) = LinearColorStop("$color${length?.let { " $it" } ?: ""}")
    }
  }

  override fun toString() = str

  companion object {
    fun url(url: String): CSSImage = Url(url)
    fun linearGradient(colorStopList: List<LinearColorStop>, direction: Direction = Direction.BOTTOM): CSSImage = LinearGradient(colorStopList, direction)
  }
}

sealed class Filter(private val str: String) {

  private class Blur(radius: Pixels) : Filter("blur($radius)")
  private class Brightness(num: Percent) : Filter("brightness($num)")
  private class BlurAndBrightness(blur: Blur, bright: Brightness) : Filter("$blur $bright")

  override fun toString() = str

  companion object {
    fun brightness(num: Percent): Filter = Brightness(num)
    fun blur(radius: Pixels): Filter = Blur(radius)
    fun blurAndBrightness(radius: Pixels, num: Percent): Filter = BlurAndBrightness(Blur(radius), Brightness(num))
  }
}

sealed class Transform(private val str: String) {

  private class ScaleX(num: Double) : Transform("scaleX($num)")

  override fun toString() = str

  companion object {
    fun scaleX(num: Double): Transform = ScaleX(num)
  }
}

enum class AlignItems {

  FLEX_START, FLEX_END, CENTER, STRETCH, BASELINE, INITIAL, INHERIT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class AlignContent {

  FLEX_START, FLEX_END, CENTER, SPACE_BETWEEN, SPACE_AROUND, SPACE_EVENLY, STRETCH;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Appearance {

  NONE;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class BackgroundPosition {

  LEFT, RIGHT, TOP, BOTTOM, CENTER;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class BackgroundRepeat {

  REPEAT, NO_REPEAT, SPACE, ROUND, INITIAL, INHERIT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class BackgroundSize {

  AUTO, COVER, CONTAIN;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class BoxSizing {

  CONTENT_BOX, BORDER_BOX;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Cursor {

  AUTO, CROSSHAIR, DEFAULT, POINTER, NONE, INHERIT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Display {

  INLINE, BLOCK, INLINE_BLOCK, HIDDEN, NONE, FLEX, TABLE, TABLE_CELL, GRID;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class FlexDirection {

  COLUMN, COLUMN_REVERSE, ROW, ROW_REVERSE;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class FlexWrap {

  NOWRAP, WRAP, WRAP_REVERSE;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Float {

  INITIAL, INHERIT, LEFT, NONE, RIGHT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class JustifyContent {

  FLEX_START, FLEX_END, CENTER, SPACE_BETWEEN, SPACE_AROUND, SPACE_EVENLY, INITIAL, INHERIT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class ObjectFit {

  CONTAIN, COVER, FILL, NONE, SCALE_DOWN;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Overflow {

  SCROLL, HIDDEN, AUTO, VISIBLE, INITIAL, INHERIT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class PointerEvents {

  AUTO, NONE, INHERIT, INITIAL, UNSET;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Position {

  STATIC, RELATIVE, FIXED, ABSOLUTE, STICKY;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class TextDecorationLine {

  NONE, UNDERLINE, OVERLINE, LINE_THROUGH, INITIAL, INHERIT;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class TextOverflow {

  CLIP, ELLIPSIS;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class VerticalAlign {

  BASELINE, BOTTOM, MIDDLE, TEXT_BOTTOM, TOP;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class Visibility {

  VISIBLE, HIDDEN, COLLAPSE;

  override fun toString() = name.lowercase().replace('_','-')
}

enum class WhiteSpace {

  NORMAL, NOWRAP, PRE, PRE_WRAP, PRE_LINE, BREAK_SPACES;

  override fun toString() = name.lowercase().replace('_','-')
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.a(init: HTMLAnchorElement.() -> Unit = {}): HTMLAnchorElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("a") {} as HTMLAnchorElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createA(init: HTMLAnchorElement.() -> Unit = {}): HTMLAnchorElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("a") as HTMLAnchorElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.audio(init: HTMLAudioElement.() -> Unit = {}): HTMLAudioElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("audio") {} as HTMLAudioElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createAudio(init: HTMLAudioElement.() -> Unit = {}): HTMLAudioElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("audio") as HTMLAudioElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.br(init: HTMLBRElement.() -> Unit = {}): HTMLBRElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("br") {} as HTMLBRElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createBr(init: HTMLBRElement.() -> Unit = {}): HTMLBRElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("br") as HTMLBRElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.button(init: HTMLButtonElement.() -> Unit = {}): HTMLButtonElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("button") {} as HTMLButtonElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createButton(init: HTMLButtonElement.() -> Unit = {}): HTMLButtonElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("button") as HTMLButtonElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.canvas(init: HTMLCanvasElement.() -> Unit = {}): HTMLCanvasElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("canvas") {} as HTMLCanvasElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createCanvas(init: HTMLCanvasElement.() -> Unit = {}): HTMLCanvasElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("canvas") as HTMLCanvasElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.div(init: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("div") {} as HTMLDivElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createDiv(init: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("div") as HTMLDivElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.form(init: HTMLFormElement.() -> Unit = {}): HTMLFormElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("form") {} as HTMLFormElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createForm(init: HTMLFormElement.() -> Unit = {}): HTMLFormElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("form") as HTMLFormElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.hr(init: HTMLHRElement.() -> Unit = {}): HTMLHRElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("hr") {} as HTMLHRElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createHr(init: HTMLHRElement.() -> Unit = {}): HTMLHRElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("hr") as HTMLHRElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.h1(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("h1") {} as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createH1(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("h1") as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.h2(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("h2") {} as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createH2(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("h2") as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.h3(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("h3") {} as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createH3(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("h3") as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.h4(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("h4") {} as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createH4(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("h4") as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.h5(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("h5") {} as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createH5(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("h5") as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.h6(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("h6") {} as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createH6(init: HTMLHeadingElement.() -> Unit = {}): HTMLHeadingElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("h6") as HTMLHeadingElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.i(init: HTMLElement.() -> Unit = {}): HTMLElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("i") {} as HTMLElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createI(init: HTMLElement.() -> Unit = {}): HTMLElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("i") as HTMLElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.iframe(init: HTMLIFrameElement.() -> Unit = {}): HTMLIFrameElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("iframe") {} as HTMLIFrameElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createIframe(init: HTMLIFrameElement.() -> Unit = {}): HTMLIFrameElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("iframe") as HTMLIFrameElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.img(init: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("img") {} as HTMLImageElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createImg(init: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("img") as HTMLImageElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.input(init: HTMLInputElement.() -> Unit = {}): HTMLInputElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("input") {} as HTMLInputElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createInput(init: HTMLInputElement.() -> Unit = {}): HTMLInputElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("input") as HTMLInputElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.label(init: HTMLLabelElement.() -> Unit = {}): HTMLLabelElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("label") {} as HTMLLabelElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createLabel(init: HTMLLabelElement.() -> Unit = {}): HTMLLabelElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("label") as HTMLLabelElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.li(init: HTMLLIElement.() -> Unit = {}): HTMLLIElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("li") {} as HTMLLIElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createLi(init: HTMLLIElement.() -> Unit = {}): HTMLLIElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("li") as HTMLLIElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.ol(init: HTMLElement.() -> Unit = {}): HTMLElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("ol") {} as HTMLElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createOl(init: HTMLElement.() -> Unit = {}): HTMLElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("ol") as HTMLElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.option(init: HTMLOptionElement.() -> Unit = {}): HTMLOptionElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("option") {} as HTMLOptionElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createOption(init: HTMLOptionElement.() -> Unit = {}): HTMLOptionElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("option") as HTMLOptionElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.p(init: HTMLParagraphElement.() -> Unit = {}): HTMLParagraphElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("p") {} as HTMLParagraphElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createP(init: HTMLParagraphElement.() -> Unit = {}): HTMLParagraphElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("p") as HTMLParagraphElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.select(init: HTMLSelectElement.() -> Unit = {}): HTMLSelectElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("select") {} as HTMLSelectElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createSelect(init: HTMLSelectElement.() -> Unit = {}): HTMLSelectElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("select") as HTMLSelectElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.span(init: HTMLSpanElement.() -> Unit = {}): HTMLSpanElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("span") {} as HTMLSpanElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createSpan(init: HTMLSpanElement.() -> Unit = {}): HTMLSpanElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("span") as HTMLSpanElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.table(init: HTMLTableElement.() -> Unit = {}): HTMLTableElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("table") {} as HTMLTableElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createTable(init: HTMLTableElement.() -> Unit = {}): HTMLTableElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("table") as HTMLTableElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.textArea(init: HTMLTextAreaElement.() -> Unit = {}): HTMLTextAreaElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("textArea") {} as HTMLTextAreaElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createTextArea(init: HTMLTextAreaElement.() -> Unit = {}): HTMLTextAreaElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("textArea") as HTMLTextAreaElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.tbody(init: HTMLTableSectionElement.() -> Unit = {}): HTMLTableSectionElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("tbody") {} as HTMLTableSectionElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createTbody(init: HTMLTableSectionElement.() -> Unit = {}): HTMLTableSectionElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("tbody") as HTMLTableSectionElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.tr(init: HTMLTableRowElement.() -> Unit = {}): HTMLTableRowElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("tr") {} as HTMLTableRowElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createTr(init: HTMLTableRowElement.() -> Unit = {}): HTMLTableRowElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("tr") as HTMLTableRowElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.td(init: HTMLTableCellElement.() -> Unit = {}): HTMLTableCellElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("td") {} as HTMLTableCellElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createTd(init: HTMLTableCellElement.() -> Unit = {}): HTMLTableCellElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("td") as HTMLTableCellElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.ul(init: HTMLElement.() -> Unit = {}): HTMLElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("ul") {} as HTMLElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createUl(init: HTMLElement.() -> Unit = {}): HTMLElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("ul") as HTMLElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.video(init: HTMLVideoElement.() -> Unit = {}): HTMLVideoElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (appendElement("video") {} as HTMLVideoElement).apply(init)
}

@OptIn(ExperimentalContracts::class)
inline fun createVideo(init: HTMLVideoElement.() -> Unit = {}): HTMLVideoElement {
  contract {
    callsInPlace(init, EXACTLY_ONCE)
  }
  return (document.createElement("video") as HTMLVideoElement).apply(init)
}

var HTMLElement.alignItems: AlignItems?
  get() = style.alignItems.let { if (it.isEmpty()) null else AlignItems.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.alignItems = value?.toString() ?: ""
  }

var HTMLElement.alignContent: AlignContent?
  get() = style.alignContent.let { if (it.isEmpty()) null else AlignContent.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.alignContent = value?.toString() ?: ""
  }

var HTMLElement.alignSelf: AlignItems?
  get() = style.alignSelf.let { if (it.isEmpty()) null else AlignItems.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.alignSelf = value?.toString() ?: ""
  }

var HTMLElement.appearance: Appearance?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.setProperty(property = "-webkit-appearance", value = value?.toString() ?: "")
    style.setProperty(property = "-moz-appearance", value = value?.toString() ?: "")
  }

var HTMLElement.bgColor: Color?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.backgroundColor = value?.toString() ?: ""
  }

var HTMLElement.backgroundPosition: BackgroundPosition?
  get() = style.backgroundPosition.let { if (it.isEmpty()) null else BackgroundPosition.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.backgroundPosition = value?.toString() ?: ""
  }

var HTMLElement.backgroundRepeat: BackgroundRepeat?
  get() = style.backgroundRepeat.let { if (it.isEmpty()) null else BackgroundRepeat.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.backgroundRepeat = value?.toString() ?: ""
  }

var HTMLElement.backgroundSize: BackgroundSize?
  get() = style.backgroundSize.let { if (it.isEmpty()) null else BackgroundSize.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.backgroundSize = value?.toString() ?: ""
  }

var HTMLElement.border: Border?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.border = value?.toString() ?: ""
  }

var HTMLElement.borderTop: Border?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderTop = value?.toString() ?: ""
  }

var HTMLElement.borderBottom: Border?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderBottom = value?.toString() ?: ""
  }

var HTMLElement.borderColor: Color?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderColor = value?.toString() ?: ""
  }

var HTMLElement.borderLeft: Border?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderLeft = value?.toString() ?: ""
  }

var HTMLElement.borderRadius: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderRadius = value?.toString() ?: ""
  }

var HTMLElement.borderTopLeftRadius: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderTopLeftRadius = value?.toString() ?: ""
  }

var HTMLElement.borderTopRightRadius: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderTopRightRadius = value?.toString() ?: ""
  }

var HTMLElement.borderBottomRightRadius: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderBottomRightRadius = value?.toString() ?: ""
  }

var HTMLElement.borderBottomLeftRadius: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderBottomLeftRadius = value?.toString() ?: ""
  }

var HTMLElement.borderRight: Border?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderRight = value?.toString() ?: ""
  }

var HTMLElement.borderWidth: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.borderWidth = value?.toString() ?: ""
  }

var HTMLElement.boxSizing: BoxSizing?
  get() = style.boxSizing.let { if (it.isEmpty()) null else BoxSizing.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.boxSizing = value?.toString() ?: ""
  }

var HTMLElement.bottom: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.bottom = value?.toString() ?: ""
  }

var HTMLElement.txtColor: Color?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.color = value?.toString() ?: ""
  }

var HTMLElement.cursor: Cursor?
  get() = style.cursor.let { if (it.isEmpty()) null else Cursor.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.cursor = value?.toString() ?: ""
  }

var HTMLElement.display: Display?
  get() = style.display.let { if (it.isEmpty()) null else Display.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.display = value?.toString() ?: ""
  }

var HTMLElement.flexBasis: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.flexBasis = value?.toString() ?: ""
  }

var HTMLElement.flexDirection: FlexDirection?
  get() = style.flexDirection.let { if (it.isEmpty()) null else FlexDirection.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.flexDirection = value?.toString() ?: ""
  }

var HTMLElement.flexGrow: Double?
  get() = style.flexGrow.toDoubleOrNull()
  set(value) {
    style.flexGrow = value?.toString() ?: ""
  }

var HTMLElement.flexShrink: Double?
  get() = style.flexShrink.toDoubleOrNull()
  set(value) {
    style.flexShrink = value?.toString() ?: ""
  }

var HTMLElement.flexWrap: FlexWrap?
  get() = style.flexWrap.let { if (it.isEmpty()) null else FlexWrap.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.flexWrap = value?.toString() ?: ""
  }

var HTMLElement.float: Float?
  get() = style.cssFloat.let { if (it.isEmpty()) null else Float.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.cssFloat = value?.toString() ?: ""
  }

var HTMLElement.filter: Filter?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.filter = value?.toString() ?: ""
  }

var HTMLElement.fontFamily: String?
  get() = style.fontFamily
  set(value) {
    style.fontFamily = value ?: ""
  }

var HTMLElement.fontSize: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.fontSize = value?.toString() ?: ""
  }

var HTMLElement.fontWeight: FontWeight?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.fontWeight = value?.toString() ?: ""
  }

var HTMLElement.height: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.height = value?.toString() ?: ""
  }

var HTMLElement.justifyContent: JustifyContent?
  get() = style.justifyContent.let { if (it.isEmpty()) null else JustifyContent.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.justifyContent = value?.toString() ?: ""
  }

var HTMLElement.left: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.left = value?.toString() ?: ""
  }

var HTMLElement.margin: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.margin = value?.toString() ?: ""
  }

var HTMLElement.marginTop: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.marginTop = value?.toString() ?: ""
  }

var HTMLElement.marginBottom: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.marginBottom = value?.toString() ?: ""
  }

var HTMLElement.marginLeft: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.marginLeft = value?.toString() ?: ""
  }

var HTMLElement.marginRight: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.marginRight = value?.toString() ?: ""
  }

var HTMLElement.mask: CSSImage?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.mask = value?.toString() ?: ""
    style.setProperty(property = "-webkit-mask", value = value?.toString() ?: "")
  }

var HTMLElement.maxHeight: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.maxHeight = value?.toString() ?: ""
  }

var HTMLElement.maxWidth: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.maxWidth = value?.toString() ?: ""
  }

var HTMLElement.minHeight: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.minHeight = value?.toString() ?: ""
  }

var HTMLElement.minWidth: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.minWidth = value?.toString() ?: ""
  }

var HTMLElement.objectFit: ObjectFit?
  get() = style.objectFit.let { if (it.isEmpty()) null else ObjectFit.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.objectFit = value?.toString() ?: ""
  }

var HTMLElement.opacity: Double?
  get() = style.opacity.toDoubleOrNull()
  set(value) {
    style.opacity = value?.toString() ?: ""
  }

var HTMLElement.overflowX: Overflow?
  get() = style.overflowX.let { if (it.isEmpty()) null else Overflow.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.overflowX = value?.toString() ?: ""
  }

var HTMLElement.overflowY: Overflow?
  get() = style.overflowY.let { if (it.isEmpty()) null else Overflow.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.overflowY = value?.toString() ?: ""
  }

var HTMLElement.padding: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.padding = value?.toString() ?: ""
  }

var HTMLElement.paddingLeft: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.paddingLeft = value?.toString() ?: ""
  }

var HTMLElement.paddingTop: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.paddingTop = value?.toString() ?: ""
  }

var HTMLElement.paddingRight: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.paddingRight = value?.toString() ?: ""
  }

var HTMLElement.paddingBottom: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.paddingBottom = value?.toString() ?: ""
  }

var HTMLElement.pointerEvents: PointerEvents?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.setProperty(property = "pointer-events", value = value?.toString() ?: "")
  }

var HTMLElement.position: Position?
  get() = style.position.let { if (it.isEmpty()) null else Position.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.position = value?.toString() ?: ""
  }

var HTMLElement.right: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.right = value?.toString() ?: ""
  }

var HTMLElement.textAlign: TextAlign?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.textAlign = value?.toString() ?: ""
  }

var HTMLElement.textDecorationLine: TextDecorationLine?
  get() = style.textDecorationLine.let { if (it.isEmpty()) null else TextDecorationLine.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.textDecorationLine = value?.toString() ?: ""
  }

var HTMLElement.textShadow: TextShadow?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.textShadow = value?.toString() ?: ""
  }

var HTMLElement.boxShadow: BoxShadow?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.boxShadow = value?.toString() ?: ""
  }

var HTMLElement.top: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.top = value?.toString() ?: ""
  }

var HTMLElement.verticalAlign: VerticalAlign?
  get() = style.verticalAlign.let { if (it.isEmpty()) null else VerticalAlign.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.verticalAlign = value?.toString() ?: ""
  }

var HTMLElement.width: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.width = value?.toString() ?: ""
  }

var HTMLElement.zIndex: ZIndex?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.zIndex = value?.toString() ?: ""
  }

var HTMLElement.transform: Transform?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.transform = value?.toString() ?: ""
  }

var HTMLElement.lineHeight: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    style.lineHeight = value?.toString() ?: ""
  }

var HTMLElement.visibility: Visibility?
  get() = style.visibility.let { if (it.isEmpty()) null else Visibility.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.visibility = value?.toString() ?: ""
  }

var HTMLElement.whiteSpace: WhiteSpace?
  get() = style.whiteSpace.let { if (it.isEmpty()) null else WhiteSpace.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.whiteSpace = value?.toString() ?: ""
  }

var HTMLElement.textOverflow: TextOverflow?
  get() = style.textOverflow.let { if (it.isEmpty()) null else TextOverflow.valueOf(it.uppercase().replace('-', '_')) }
  set(value) {
    style.textOverflow = value?.toString() ?: ""
  }

fun HTMLElement.onClick(cursorPointer: Boolean = true, block: (Event) -> Unit = {}) {
  this.onclick = { e ->
    e.stopPropagation()
    block(e)
    null
  }
  if (cursorPointer) {
    tabIndex = 0
    this.onkeydown = { e ->
      if (KeyStroke.fromCode(e.code) == KeyStroke.ENTER) {
        block(e)
      }
    }
    cursor = Cursor.POINTER
  }
}

var HTMLElement.marginHorizontal: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    marginLeft = value
    marginRight = value
  }

var HTMLElement.marginVertical: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    marginTop = value
    marginBottom = value
  }

var HTMLElement.paddingHorizontal: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    paddingLeft = value
    paddingRight = value
  }

var HTMLElement.paddingVertical: Size?
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    paddingTop = value
    paddingBottom = value
  }

var HTMLElement.ariaLabel: String
  @Deprecated(level = DeprecationLevel.ERROR, message = "Non readable property")
  get() = throw UnsupportedOperationException()
  set(value) {
    setAttribute("aria-label", value)
  }

fun HTMLElement.show() {
  if (display == NONE) {
    style.display = getAttribute("displayWhenHidden") ?: ""
    setAttribute("aria-hidden", "false")
  }
}

fun HTMLElement.hide() {
  if (display != NONE) {
    setAttribute("displayWhenHidden", style.display)
    setAttribute("aria-hidden", "true")
    display = NONE
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.centerInParent(blockClicks: Boolean = true, block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return horizontalFlexBox(horizontalJustify = JustifyContent.CENTER, verticalAlign = AlignItems.CENTER) {
    height = percent(100)
    width = percent(100)

    div {
      display = INLINE
      if (blockClicks) {
        onClick(cursorPointer = false) // Prevents clicking inner div from activating outer click event
      }

      block()
    }
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.centerVertically(block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
    height = INHERIT
    block()
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.centerHorizontally(block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return verticalFlexBox(horizontalAlign = AlignItems.CENTER, block = block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.horizontalFlexBox(horizontalJustify: JustifyContent? = null,
                                         verticalAlign: AlignItems? = null,
                                         flexWrap: FlexWrap? = null,
                                         block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return flexBoxInternal(direction = FlexDirection.ROW, justifyContent = horizontalJustify, alignItems = verticalAlign, flexWrap = flexWrap, block = block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.verticalFlexBox(verticalJustify: JustifyContent? = null,
                                       horizontalAlign: AlignItems? = null,
                                       flexWrap: FlexWrap? = null,
                                       block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return flexBoxInternal(direction = FlexDirection.COLUMN, justifyContent = verticalJustify, alignItems = horizontalAlign, flexWrap = flexWrap, block = block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.flexBoxInternal(direction: FlexDirection,
                                       justifyContent: JustifyContent?,
                                       alignItems: AlignItems?,
                                       flexWrap: FlexWrap?,
                                       block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return div {
    display = Display.FLEX
    flexDirection = direction
    alignItems?.let {
      this.alignItems = it
    }
    justifyContent?.let {
      this.justifyContent = it
    }
    flexWrap?.let {
      this.flexWrap = it
    }
    block()
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.label(text: String? = null,
                             fontSize: Int,
                             fontWeight: FontWeight = FontWeight.REGULAR,
                             color: Color = WHITE,
                             textAlign: TextAlign? = null,
                             block: HTMLSpanElement.() -> Unit = {}): HTMLSpanElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return span {
    txtColor = color
    text?.let {
      textContent = text
    }
    textAlign?.let {
      this.textAlign = it
    }
    this.fontSize = px(fontSize)
    this.fontWeight = fontWeight
    fontFamily = FONT_FAMILY
    cursor = Cursor.INHERIT
    block()
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.link(text: String, href: String, fontSize: Int = 16, block: HTMLAnchorElement.() -> Unit = {}): HTMLAnchorElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return a {
    this.text = text
    this.href = href
    this.fontSize = px(fontSize)
    txtColor = Color.LINK_COLOR
    target = "_blank"
    block()
  }
}

val FONT_FAMILY = "'Montserrat', sans-serif"
